<template>
  <main dir="rtl" class="flex-1 pb-8">
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 class="text-2xl font-bold text-gray-900">إدارة جداول الجلسات</h2>
        <div class="flex gap-2">
          <button
            @click="loadSchedules"
            class="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg"
          >
            تحديث
          </button>
          <button
            v-if="canCreate"
            @click="openCreateModal"
            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            إنشاء جدول الجلسات
          </button>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-4 mb-6">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            v-model="filters.q"
            type="text"
            placeholder="بحث بالعنوان أو الوصف"
            class="w-full p-2 border border-gray-300 rounded-md"
          />
          <select v-model="filters.is_active" class="w-full p-2 border border-gray-300 rounded-md">
            <option value="">كل الحالات</option>
            <option value="true">نشط</option>
            <option value="false">غير نشط</option>
          </select>
          <button
            @click="loadSchedules"
            class="bg-primary hover:bg-primary/90 text-white rounded-md px-4 py-2"
          >
            تطبيق الفلتر
          </button>
        </div>
      </div>

      <div v-if="loading" class="text-center py-8 text-gray-600">جاري التحميل...</div>

      <div v-else class="bg-white rounded-lg shadow overflow-x-auto mb-6">
        <table class="min-w-full">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">العنوان</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">الفترة</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">النوع</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">التسعير</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">الحالة</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">معلمين</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">أيام</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">إجراءات</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr
              v-for="schedule in schedules"
              :key="schedule.id"
              class="hover:bg-gray-50 cursor-pointer"
              @click="selectSchedule(schedule)"
            >
              <td class="px-4 py-3 text-sm font-medium text-gray-800 text-center">{{ schedule.title }}</td>
              <td class="px-4 py-3 text-sm text-gray-600 text-center">{{ formatDate(schedule.start_date) }} - {{ formatDate(schedule.end_date) }}</td>
              <td class="px-4 py-3 text-sm text-gray-600 text-center">{{ scheduleTypeLabel(schedule.schedule_type) }}</td>
              <td class="px-4 py-3 text-sm text-center">
                <span :class="schedule.is_paid ? 'text-blue-700 bg-blue-100' : 'text-green-700 bg-green-100'" class="px-2 py-1 rounded text-xs">
                  {{ schedule.is_paid ? 'مدفوع' : 'مجاني' }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-center">
                <span :class="schedule.is_active ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'" class="px-2 py-1 rounded text-xs">
                  {{ schedule.is_active ? 'نشط' : 'غير نشط' }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-gray-600 text-center">{{ schedule.teachers?.length || 0 }}</td>
              <td class="px-4 py-3 text-sm text-gray-600 text-center">{{ schedule.days?.length || 0 }}</td>
              <td class="px-4 py-3 text-sm">
                <div class="flex justify-center gap-2">
                  <button
                    v-if="canUpdate"
                    @click.stop="openEditModal(schedule)"
                    class="text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded"
                  >
                    تعديل
                  </button>
                  <button
                    v-if="canDelete"
                    @click.stop="deleteSchedule(schedule)"
                    class="text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1 rounded"
                  >
                    حذف
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="schedules.length === 0">
              <td colspan="8" class="px-4 py-6 text-center text-gray-500">لا توجد جداول الجلسات حالياً</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="selectedSchedule" class="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div class="bg-white rounded-lg shadow p-4">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">المعلمون المرتبطون</h3>
          <div class="flex gap-2 mb-3" v-if="canCreate || canUpdate">
            <select v-model="teacherForm.teacher_id" class="flex-1 p-2 border border-gray-300 rounded-md">
              <option value="">اختر المعلم</option>
              <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
                {{ teacher.fullName || teacher.full_name || `#${teacher.id}` }}
              </option>
            </select>
            <select v-model="teacherForm.role_type" class="p-2 border border-gray-300 rounded-md">
              <option value="primary">أساسي</option>
              <option value="assistant">مساعد</option>
              <option value="substitute">بديل</option>
            </select>
            <button @click="assignTeacher" class="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md">
              ربط
            </button>
          </div>
          <div class="space-y-2">
            <div v-for="item in selectedSchedule.teachers || []" :key="item.id" class="flex items-center justify-between bg-gray-50 rounded p-2">
              <div class="text-sm">
                <div class="font-medium text-gray-800">{{ item.teacher?.full_name || item.teacher?.fullName || `#${item.teacher_id}` }}</div>
                <div class="text-gray-500 text-xs">{{ teacherRoleLabel(item.role_type) }}</div>
              </div>
              <button v-if="canDelete" @click="removeTeacher(item.teacher_id)" class="text-red-600 text-sm">إزالة</button>
            </div>
            <p v-if="!(selectedSchedule.teachers || []).length" class="text-sm text-gray-500">لا يوجد معلمون مرتبطون</p>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-4">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">أيام الجدول</h3>
          <div class="flex gap-2 mb-3" v-if="canCreate || canUpdate">
            <select v-model.number="dayForm.weekday" class="flex-1 p-2 border border-gray-300 rounded-md">
              <option :value="7">الأحد</option>
              <option :value="1">الإثنين</option>
              <option :value="2">الثلاثاء</option>
              <option :value="3">الأربعاء</option>
              <option :value="4">الخميس</option>
              <option :value="5">الجمعة</option>
              <option :value="6">السبت</option>
              
            </select>
            <button @click="addDay" class="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md">إضافة</button>
          </div>
          <div class="space-y-2">
            <div
              v-for="day in sortedSelectedScheduleDays"
              :key="day.id"
              class="flex items-center justify-between rounded p-2 border"
              :class="selectedDayId === day.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200'"
            >
              <div class="flex items-center gap-3 flex-1">
                <label class="text-xs flex items-center gap-1">
                  <input
                    type="checkbox"
                    :checked="isDayBatchSelected(day.id)"
                    @change="toggleDayBatchSelection(day.id)"
                  />
                  تحديد
                </label>
                <button class="text-sm text-right flex-1" @click="selectDay(day.id)">
                  {{ weekdayLabel(day.weekday) }}
                </button>
              </div>
              <div class="flex items-center gap-3">
                <label class="text-xs flex items-center gap-2">
                  
                  <button
                    type="button"
                    role="switch"
                    :aria-checked="day.is_active"
                    dir="ltr"
                    @click.stop="toggleDayActive(day)"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200"
                    :class="day.is_active ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'"
                  >
                    <span
                      class="mx-1 inline-block h-4 w-4 rounded-full bg-white shadow-sm"
                    />
                  </button>
                </label>
                <button v-if="canDelete" @click="deleteDay(day)" class="text-red-600 text-sm">حذف</button>
              </div>
            </div>
            <p v-if="!(selectedSchedule.days || []).length" class="text-sm text-gray-500">لا توجد أيام في هذا الجدول</p>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-4">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">فترات اليوم</h3>
          <div v-if="selectedDay || selectedDayIds.length" class="space-y-3">
            <div class="grid grid-cols-1 gap-2" v-if="canCreate || canUpdate">
              <div class="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md p-2 text-xs text-gray-700">
                <span>
                  {{ selectedDayIds.length ? `سيتم الإضافة إلى ${selectedDayIds.length} يوم محدد` : 'سيتم الإضافة إلى اليوم المختار فقط' }}
                </span>
                <div class="flex items-center gap-2">
                  <button type="button" class="text-blue-700 hover:underline" @click="selectAllDaysForSlot">تحديد كل الأيام</button>
                  <button type="button" class="text-gray-700 hover:underline" @click="clearDaysForSlot">إلغاء التحديد</button>
                </div>
              </div>
              <input v-model="slotForm.slot_name" type="text" placeholder="اسم الفترة" class="p-2 border border-gray-300 rounded-md" />
              <div class="grid grid-cols-2 gap-2">
                <input v-model.number="slotForm.slot_order" type="number" min="1" placeholder="الترتيب" class="p-2 border border-gray-300 rounded-md" />
                <input v-model.number="slotForm.duration_minutes" type="number" min="1" placeholder="المدة بالدقائق" class="p-2 border border-gray-300 rounded-md" />
              </div>
              <div class="grid grid-cols-2 gap-2">
                <select v-model="slotForm.start_mode" class="p-2 border border-gray-300 rounded-md">
                  <option value="fixed_time">وقت ثابت</option>
                  <option value="prayer_offset">بالاعتماد على الصلاة</option>
                </select>
                <input v-if="slotForm.start_mode === 'fixed_time'" v-model="slotForm.fixed_start_time" type="time" class="p-2 border border-gray-300 rounded-md" />
                <select v-else v-model="slotForm.prayer_name" class="p-2 border border-gray-300 rounded-md">
                  <option value="fajr">الفجر</option>
                  <option value="dhuhr">الظهر</option>
                  <option value="asr">العصر</option>
                  <option value="maghrib">المغرب</option>
                  <option value="isha">العشاء</option>
                </select>
              </div>
              <input
                v-if="slotForm.start_mode === 'prayer_offset'"
                v-model.number="slotForm.offset_minutes"
                type="number"
                placeholder="الإزاحة بالدقائق"
                class="p-2 border border-gray-300 rounded-md"
              />
              <div v-if="editingSlotId" class="grid grid-cols-1 gap-2 p-2 bg-blue-50 border border-blue-100 rounded-md">
                <div class="text-xs text-blue-800">وضع التعديل</div>
                <div class="flex flex-wrap items-center gap-3 text-xs">
                  <label class="flex items-center gap-1">
                    <input v-model="slotEditApplyMode" type="radio" value="single" />
                    تعديل اليوم الحالي فقط
                  </label>
                  <label class="flex items-center gap-1" :class="selectedDayIds.length ? '' : 'text-gray-400'">
                    <input
                      v-model="slotEditApplyMode"
                      type="radio"
                      value="selected"
                      :disabled="!selectedDayIds.length"
                    />
                    تعديل الأيام المحددة ({{ selectedDayIds.length }})
                  </label>
                </div>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  @click="editingSlotId ? saveSlotUpdate() : addSlot()"
                  class="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md"
                >
                  {{ editingSlotId ? 'حفظ التعديل' : 'إضافة فترة' }}
                </button>
                <button
                  v-if="editingSlotId"
                  @click="cancelSlotEditing"
                  class="bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 rounded-md"
                >
                  إلغاء التعديل
                </button>
              </div>
            </div>

            <div class="space-y-2">
              <div v-for="slot in selectedDay.slots || []" :key="slot.id" class="border border-gray-200 rounded p-2">
                <div class="flex items-center justify-between">
                  <div>
                    <div class="text-sm font-medium text-gray-800">{{ slot.slot_name }} ({{ slot.slot_order }})</div>
                    <div class="text-xs text-gray-500">
                      {{ slotStartModeLabel(slot.start_mode) }}
                      <span v-if="slot.start_mode === 'prayer_offset'"> ({{ prayerNameLabel(slot.prayer_name) }})</span>
                      - {{ slot.duration_minutes }} دقيقة
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <label class="text-xs flex items-center gap-1">
                      <input type="checkbox" :checked="slot.is_active" @change="toggleSlotActive(slot)" />
                      نشط
                    </label>
                    <button v-if="canUpdate" @click="startSlotEditing(slot)" class="text-blue-700 text-sm">تعديل</button>
                    <button v-if="canDelete" @click="deleteSlot(slot)" class="text-red-600 text-sm">حذف</button>
                  </div>
                </div>
              </div>
              <p v-if="!(selectedDay.slots || []).length" class="text-sm text-gray-500">لا توجد فترات لليوم المحدد</p>
            </div>
          </div>
          <p v-else class="text-sm text-gray-500">اختر يومًا أولًا لإدارة الفترات</p>
        </div>
      </div>

      <div v-if="showScheduleModal" class="fixed inset-0 bg-black bg-opacity-50 z-50" @click.self="closeScheduleModal">
        <div class="bg-white rounded-xl max-w-xl mx-auto mt-20 p-5">
          <h3 class="text-lg font-semibold mb-4">{{ editingScheduleId ? 'تعديل جدول الجلسات' : 'إنشاء جدول الجلسات' }}</h3>
          <div class="space-y-3">
            <input v-model="scheduleForm.title" type="text" placeholder="عنوان الجدول" class="w-full p-2 border border-gray-300 rounded-md" />
            <textarea v-model="scheduleForm.description" placeholder="الوصف" class="w-full p-2 border border-gray-300 rounded-md"></textarea>
            <div class="grid grid-cols-2 gap-2">
              <input v-model="scheduleForm.start_date" type="date" class="w-full p-2 border border-gray-300 rounded-md" />
              <input v-model="scheduleForm.end_date" type="date" class="w-full p-2 border border-gray-300 rounded-md" />
            </div>
            <div class="grid grid-cols-3 gap-2">
              <select v-model="scheduleForm.schedule_type" class="w-full p-2 border border-gray-300 rounded-md">
                <option value="mixed">مختلط</option>
                <option value="fixed">ثابت</option>
                <option value="prayer_based">مرتبط بالصلاة</option>
              </select>
              <label class="flex items-center justify-center gap-2 border border-gray-300 rounded-md">
                <input v-model="scheduleForm.is_paid" type="checkbox" />
                مدفوع
              </label>
              <label class="flex items-center justify-center gap-2 border border-gray-300 rounded-md">
                <input v-model="scheduleForm.is_active" type="checkbox" />
                نشط
              </label>
            </div>
          </div>
          <div class="flex gap-2 mt-4">
            <button @click="saveSchedule" class="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md">
              حفظ
            </button>
            <button @click="closeScheduleModal" class="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md">
              إلغاء
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { defineComponent } from 'vue';
import { usePermissions } from '@/composables/usePermissions';
import {
  assignIqraTeacher,
  createIqraDay,
  createIqraSchedule,
  createIqraSlot,
  deleteIqraDay,
  deleteIqraSchedule,
  deleteIqraSlot,
  getIqraSchedule,
  listIqraSchedules,
  listTeachersForIqra,
  removeIqraTeacher,
  updateIqraDay,
  updateIqraSchedule,
  updateIqraSlot,
} from '@/services/iqraSchedules';

export default defineComponent({
  name: 'IqraSchedulesManagement',
  setup() {
    const permissions = usePermissions();
    return { permissions };
  },
  data() {
    return {
      loading: false,
      schedules: [],
      pagination: { current_page: 1, per_page: 20, total: 0 },
      filters: {
        q: '',
        is_active: '',
      },
      teachers: [],
      selectedSchedule: null,
      selectedDayId: null,
      selectedDayIds: [],
      showScheduleModal: false,
      editingScheduleId: null,
      scheduleForm: {
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        schedule_type: 'mixed',
        is_paid: true,
        is_active: true,
      },
      teacherForm: {
        teacher_id: '',
        role_type: 'primary',
      },
      dayForm: {
        weekday: 7,
      },
      slotForm: {
        slot_name: '',
        slot_order: 1,
        start_mode: 'fixed_time',
        fixed_start_time: '',
        prayer_name: 'fajr',
        offset_minutes: 0,
        duration_minutes: 30,
      },
      editingSlotId: null,
      editingSlotSourceOrder: null,
      slotEditApplyMode: 'single',
    };
  },
  computed: {
    canRead() {
      return this.permissions.hasPermission('iqra_schedules', 'read');
    },
    canCreate() {
      return this.permissions.hasPermission('iqra_schedules', 'create');
    },
    canUpdate() {
      return this.permissions.hasPermission('iqra_schedules', 'update');
    },
    canDelete() {
      return this.permissions.hasPermission('iqra_schedules', 'delete');
    },
    selectedDay() {
      if (!this.selectedSchedule || !this.selectedDayId) return null;
      return (this.selectedSchedule.days || []).find((d) => d.id === this.selectedDayId) || null;
    },
    sortedSelectedScheduleDays() {
      if (!this.selectedSchedule) return [];
      const days = [...(this.selectedSchedule.days || [])];
      return days.sort((a, b) => this.daySortWeight(a.weekday) - this.daySortWeight(b.weekday));
    },
  },
  async mounted() {
    await this.permissions.fetchUserPermissions();
    await this.loadTeachers();
    await this.loadSchedules();
  },
  methods: {
    async loadSchedules() {
      this.loading = true;
      try {
        const result = await listIqraSchedules({
          q: this.filters.q || undefined,
          is_active: this.filters.is_active || undefined,
          page: this.pagination.current_page,
          size: this.pagination.per_page,
        });
        this.schedules = result.data || [];
        this.pagination = result.pagination || this.pagination;

        if (this.selectedSchedule) {
          const refreshed = this.schedules.find((s) => s.id === this.selectedSchedule.id);
          this.selectedSchedule = refreshed || null;
          if (!this.selectedSchedule) this.selectedDayId = null;
        }
      } catch (error) {
        alert(error.message);
      } finally {
        this.loading = false;
      }
    },
    async loadTeachers() {
      try {
        this.teachers = await listTeachersForIqra();
      } catch (error) {
        alert(error.message);
      }
    },
    async selectSchedule(schedule) {
      try {
        const full = await getIqraSchedule(schedule.id);
        this.selectedSchedule = full || schedule;
        const firstDay = this.sortedSelectedScheduleDays[0];
        this.selectedDayId = firstDay ? firstDay.id : null;
        this.selectedDayIds = [];
      } catch (error) {
        alert(error.message);
      }
    },
    selectDay(dayId) {
      this.selectedDayId = dayId;
    },
    toggleDayBatchSelection(dayId) {
      const value = Number(dayId);
      if (this.selectedDayIds.includes(value)) {
        this.selectedDayIds = this.selectedDayIds.filter((id) => id !== value);
      } else {
        this.selectedDayIds = [...this.selectedDayIds, value];
      }
    },
    isDayBatchSelected(dayId) {
      return this.selectedDayIds.includes(Number(dayId));
    },
    selectAllDaysForSlot() {
      this.selectedDayIds = this.sortedSelectedScheduleDays.map((day) => day.id);
    },
    clearDaysForSlot() {
      this.selectedDayIds = [];
    },
    openCreateModal() {
      this.editingScheduleId = null;
      this.scheduleForm = {
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        schedule_type: 'mixed',
        is_paid: true,
        is_active: true,
      };
      this.showScheduleModal = true;
    },
    openEditModal(schedule) {
      this.editingScheduleId = schedule.id;
      this.scheduleForm = {
        title: schedule.title || '',
        description: schedule.description || '',
        start_date: this.normalizeDateInput(schedule.start_date),
        end_date: this.normalizeDateInput(schedule.end_date),
        schedule_type: schedule.schedule_type || 'mixed',
        is_paid: schedule.is_paid !== false,
        is_active: !!schedule.is_active,
      };
      this.showScheduleModal = true;
    },
    closeScheduleModal() {
      this.showScheduleModal = false;
    },
    async saveSchedule() {
      try {
        if (this.editingScheduleId) {
          await updateIqraSchedule(this.editingScheduleId, this.scheduleForm);
        } else {
          await createIqraSchedule(this.scheduleForm);
        }

        this.closeScheduleModal();
        await this.loadSchedules();
      } catch (error) {
        alert(error.message);
      }
    },
    async deleteSchedule(schedule) {
      if (!confirm(`تأكيد حذف جدول الجلسات: ${schedule.title}`)) return;
      try {
        await deleteIqraSchedule(schedule.id);
        if (this.selectedSchedule?.id === schedule.id) {
          this.selectedSchedule = null;
          this.selectedDayId = null;
        }
        await this.loadSchedules();
      } catch (error) {
        alert(error.message);
      }
    },
    async assignTeacher() {
      if (!this.selectedSchedule) return;
      if (!this.teacherForm.teacher_id) {
        alert('اختر المعلم أولاً');
        return;
      }
      try {
        await assignIqraTeacher(this.selectedSchedule.id, {
          teacher_id: Number(this.teacherForm.teacher_id),
          role_type: this.teacherForm.role_type,
          is_active: true,
        });
        this.teacherForm.teacher_id = '';
        await this.refreshSelectedSchedule();
      } catch (error) {
        alert(error.message);
      }
    },
    async removeTeacher(teacherId) {
      if (!this.selectedSchedule) return;
      if (!confirm('تأكيد إزالة المعلم من جدول الجلسات؟')) return;
      try {
        await removeIqraTeacher(this.selectedSchedule.id, teacherId);
        await this.refreshSelectedSchedule();
      } catch (error) {
        alert(error.message);
      }
    },
    async addDay() {
      if (!this.selectedSchedule) return;
      try {
        await createIqraDay(this.selectedSchedule.id, {
          weekday: this.dayForm.weekday,
          is_active: true,
        });
        await this.refreshSelectedSchedule();
      } catch (error) {
        alert(error.message);
      }
    },
    async toggleDayActive(day) {
      if (!this.selectedSchedule) return;
      try {
        await updateIqraDay(this.selectedSchedule.id, day.id, {
          is_active: !day.is_active,
        });
        await this.refreshSelectedSchedule();
      } catch (error) {
        alert(error.message);
      }
    },
    async deleteDay(day) {
      if (!this.selectedSchedule) return;
      if (!confirm('تأكيد حذف يوم الجدول؟ سيتم حذف الفترات التابعة له.')) return;
      try {
        await deleteIqraDay(this.selectedSchedule.id, day.id);
        if (this.selectedDayId === day.id) this.selectedDayId = null;
        this.selectedDayIds = this.selectedDayIds.filter((id) => id !== Number(day.id));
        await this.refreshSelectedSchedule();
      } catch (error) {
        alert(error.message);
      }
    },
    async addSlot() {
      const targetDayIds = this.selectedDayIds.length
        ? this.selectedDayIds
        : this.selectedDay
          ? [this.selectedDay.id]
          : [];
      if (!targetDayIds.length) {
        alert('اختر يومًا واحدًا أو أكثر قبل إضافة الفترة');
        return;
      }
      try {
        const payload = this.buildSlotPayload();
        payload.is_active = true;

        await Promise.all(targetDayIds.map((dayId) => createIqraSlot(dayId, payload)));
        this.resetSlotForm();
        this.selectedDayIds = [];
        await this.refreshSelectedSchedule();
        alert(targetDayIds.length > 1 ? `تمت إضافة الفترة إلى ${targetDayIds.length} أيام` : 'تمت إضافة الفترة لليوم المحدد');
      } catch (error) {
        alert(error.message);
      }
    },
    startSlotEditing(slot) {
      this.editingSlotId = Number(slot.id);
      this.editingSlotSourceOrder = Number(slot.slot_order);
      this.slotEditApplyMode = 'single';
      this.slotForm.slot_name = slot.slot_name || '';
      this.slotForm.slot_order = Number(slot.slot_order) || 1;
      this.slotForm.start_mode = slot.start_mode || 'fixed_time';
      this.slotForm.fixed_start_time = slot.fixed_start_time || '';
      this.slotForm.prayer_name = slot.prayer_name || 'fajr';
      this.slotForm.offset_minutes = Number(slot.offset_minutes ?? 0);
      this.slotForm.duration_minutes = Number(slot.duration_minutes) || 30;
    },
    cancelSlotEditing() {
      this.editingSlotId = null;
      this.editingSlotSourceOrder = null;
      this.slotEditApplyMode = 'single';
      this.resetSlotForm();
    },
    buildSlotPayload() {
      const payload = {
        slot_name: this.slotForm.slot_name,
        slot_order: Number(this.slotForm.slot_order),
        start_mode: this.slotForm.start_mode,
        duration_minutes: Number(this.slotForm.duration_minutes),
      };

      if (this.slotForm.start_mode === 'fixed_time') {
        payload.fixed_start_time = this.slotForm.fixed_start_time;
      } else {
        payload.prayer_name = this.slotForm.prayer_name;
        payload.offset_minutes = Number(this.slotForm.offset_minutes);
      }

      return payload;
    },
    resetSlotForm() {
      this.slotForm.slot_name = '';
      this.slotForm.slot_order = 1;
      this.slotForm.start_mode = 'fixed_time';
      this.slotForm.fixed_start_time = '';
      this.slotForm.prayer_name = 'fajr';
      this.slotForm.offset_minutes = 0;
      this.slotForm.duration_minutes = 30;
    },
    async saveSlotUpdate() {
      if (!this.selectedDay || !this.editingSlotId) return;

      const targetDayIds = this.slotEditApplyMode === 'selected' && this.selectedDayIds.length
        ? this.selectedDayIds
        : [this.selectedDay.id];

      try {
        const payload = this.buildSlotPayload();
        const updateRequests = [];
        const missingDays = [];

        targetDayIds.forEach((dayId) => {
          const day = (this.selectedSchedule?.days || []).find((item) => Number(item.id) === Number(dayId));
          if (!day) {
            missingDays.push(String(dayId));
            return;
          }

          let targetSlot = null;
          if (Number(day.id) === Number(this.selectedDay.id)) {
            targetSlot = (day.slots || []).find((item) => Number(item.id) === Number(this.editingSlotId));
          }

          if (!targetSlot) {
            targetSlot = (day.slots || []).find(
              (item) => Number(item.slot_order) === Number(this.editingSlotSourceOrder)
            );
          }

          if (!targetSlot) {
            missingDays.push(this.weekdayLabel(day.weekday));
            return;
          }

          updateRequests.push(updateIqraSlot(day.id, targetSlot.id, payload));
        });

        if (!updateRequests.length) {
          throw new Error('تعذر العثور على الفترة المطلوب تعديلها في الأيام المحددة');
        }

        await Promise.all(updateRequests);
        const updatedCount = updateRequests.length;
        await this.refreshSelectedSchedule();
        this.cancelSlotEditing();

        if (missingDays.length) {
          alert(`تم تعديل الفترة في ${updatedCount} يوم، ولم يتم العثور عليها في: ${missingDays.join('، ')}`);
        } else {
          alert(updatedCount > 1 ? `تم تعديل الفترة في ${updatedCount} أيام` : 'تم تعديل الفترة لليوم المحدد');
        }
      } catch (error) {
        alert(error.message);
      }
    },
    async toggleSlotActive(slot) {
      if (!this.selectedDay) return;
      try {
        await updateIqraSlot(this.selectedDay.id, slot.id, { is_active: !slot.is_active });
        await this.refreshSelectedSchedule();
      } catch (error) {
        alert(error.message);
      }
    },
    async deleteSlot(slot) {
      if (!this.selectedDay) return;
      if (!confirm('تأكيد حذف الفترة؟')) return;
      try {
        await deleteIqraSlot(this.selectedDay.id, slot.id);
        await this.refreshSelectedSchedule();
      } catch (error) {
        alert(error.message);
      }
    },
    async refreshSelectedSchedule() {
      if (!this.selectedSchedule) return;
      const full = await getIqraSchedule(this.selectedSchedule.id);
      this.selectedSchedule = full || this.selectedSchedule;

      if (this.selectedDayId) {
        const exists = (this.selectedSchedule.days || []).some((d) => d.id === this.selectedDayId);
        if (!exists) {
          this.selectedDayId = this.sortedSelectedScheduleDays[0]?.id || null;
        }
      } else {
        this.selectedDayId = this.sortedSelectedScheduleDays[0]?.id || null;
      }
      const allowedIds = new Set((this.selectedSchedule.days || []).map((day) => Number(day.id)));
      this.selectedDayIds = this.selectedDayIds.filter((id) => allowedIds.has(Number(id)));
      if (this.editingSlotId) {
        const selectedDaySlots = this.selectedDay?.slots || [];
        const stillExists = selectedDaySlots.some((slot) => Number(slot.id) === Number(this.editingSlotId));
        if (!stillExists) {
          this.cancelSlotEditing();
        }
      }

      await this.loadSchedules();
    },
    weekdayLabel(value) {
      const map = {
        1: 'الإثنين',
        2: 'الثلاثاء',
        3: 'الأربعاء',
        4: 'الخميس',
        5: 'الجمعة',
        6: 'السبت',
        7: 'الأحد',
      };
      return map[value] || `اليوم ${value}`;
    },
    daySortWeight(weekday) {
      const order = { 7: 0, 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6 };
      return order[weekday] ?? 99;
    },
    scheduleTypeLabel(value) {
      const map = {
        mixed: 'مختلط',
        fixed: 'ثابت',
        prayer_based: 'مرتبط بالصلاة',
      };
      return map[value] || value || '-';
    },
    teacherRoleLabel(value) {
      const map = {
        primary: 'أساسي',
        assistant: 'مساعد',
        substitute: 'بديل',
      };
      return map[value] || value || '-';
    },
    slotStartModeLabel(value) {
      const map = {
        fixed_time: 'وقت ثابت',
        prayer_offset: 'بالاعتماد على الصلاة',
      };
      return map[value] || value || '-';
    },
    prayerNameLabel(value) {
      const map = {
        fajr: 'الفجر',
        dhuhr: 'الظهر',
        asr: 'العصر',
        maghrib: 'المغرب',
        isha: 'العشاء',
      };
      return map[value] || value || '-';
    },
    formatDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString('ar-SA');
    },
    normalizeDateInput(value) {
      if (!value) return '';
      if (typeof value === 'string') {
        return value.slice(0, 10);
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return date.toISOString().slice(0, 10);
    },
  },
});
</script>

